import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useState } from "react";
import styled from "styled-components";
import JsonEditor from "./JsonEditor";
import {
  AWSAISettings,
  DeepseekAISettings,
  ModelProvider,
  OpenaiAISettings,
  TenantRecord,
  UpdateTenantDTO,
} from "../service";

// Form values type that includes dayjs for date field and string settings
type EditTenantFormValues = Omit<
  UpdateTenantDTO,
  | "valid_until"
  | "aws_settings"
  | "openai_settings"
  | "genbi_settings"
  | "deepseek_settings"
> & {
  valid_until?: Dayjs;
  aws_settings: string;
  openai_settings: string;
  deepseek_settings: string;
  genbi_settings: string;
};

interface ParsedSettings {
  awsSettings?: AWSAISettings;
  openaiSettings?: OpenaiAISettings;
  genbiSettings?: any;
  deepseekSettings?: DeepseekAISettings;
}

const StyledForm = styled(Form<EditTenantFormValues>)`
  .ant-form-item-label {
    font-weight: 500;
  }
`;

const StyledDivider = styled(Divider)`
  &.ant-divider-with-text {
    margin: 24px 0 16px;
  }
`;

export interface EditTenantFormProps {
  tenant: TenantRecord;
  onSubmit?: (values: UpdateTenantDTO) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
  readonly?: boolean;
}

const EditTenantForm: React.FC<EditTenantFormProps> = ({
  tenant,
  onSubmit,
  onCancel,
  submitting = false,
  readonly = false,
}) => {
  const [form] = Form.useForm<EditTenantFormValues>();
  const [selectedProvider, setSelectedProvider] = useState<ModelProvider>(
    tenant.model_provider,
  );
  const [awsSettingsError, setAwsSettingsError] = useState<string>();
  const [openaiSettingsError, setOpenaiSettingsError] = useState<string>();
  const [genbiSettingsError, setGenbiSettingsError] = useState<string>();
  const [deepseekSettingsError, setDeepseekSettingsError] = useState<string>();

  // Initialize form with tenant data
  React.useEffect(() => {
    form.setFieldsValue({
      tenant_name: tenant.tenant_name,
      tenant_code: tenant.tenant_code,
      max_users: tenant.max_users,
      valid_until: dayjs(tenant.valid_until),
      model_provider: tenant.model_provider,
      status: tenant.status,
      aws_settings: JSON.stringify(
        tenant.aws_settings || {
          aws_access_key: "",
          aws_secret_key: "",
          aws_assume_role_arn: "",
          aws_region: "",
          models: [],
        },
        null,
        2,
      ),
      openai_settings: JSON.stringify(
        tenant.openai_settings || {
          openai_api_key: "",
          openai_project: "",
          models: [],
        },
        null,
        2,
      ),

      deepseek_settings: JSON.stringify(
        tenant.deepseek_settings || {
          deepseek_api_key: "",
          models: [],
        },
        null,
        2,
      ),
      genbi_settings: JSON.stringify(
        tenant.genbi_settings || {
          openai_api_key: "",
          openai_project: "",
          models: [],
        },
        null,
        2,
      ),
    });
    setSelectedProvider(tenant.model_provider);
  }, [tenant, form]);

  const parseSettings = (jsonString: string): any => {
    if (!jsonString.trim()) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(jsonString);
      // Remove empty string values
      Object.keys(parsed).forEach((key) => {
        if (parsed[key] === "") {
          delete parsed[key];
        }
      });
      // If all values are empty, return undefined
      return Object.keys(parsed).length > 0 ? parsed : undefined;
    } catch (error) {
      throw new Error("Invalid JSON format");
    }
  };

  const validateAISettings = async (): Promise<ParsedSettings> => {
    try {
      const awsSettingsStr = form.getFieldValue("aws_settings");
      const openaiSettingsStr = form.getFieldValue("openai_settings");
      const deepseekSettingsStr = form.getFieldValue("deepseek_settings");
      const genbiSettingsStr = form.getFieldValue("genbi_settings");

      const awsSettings = parseSettings(awsSettingsStr);
      const openaiSettings = parseSettings(openaiSettingsStr);
      const deepseekSettings = parseSettings(deepseekSettingsStr);
      const genbiSettings = parseSettings(genbiSettingsStr);

      // Only validate the selected provider's settings
      if (selectedProvider === "aws") {
        if (!awsSettings?.aws_access_key || !awsSettings?.aws_secret_key) {
          throw new Error(
            "AWS settings require both access key and secret key",
          );
        }
        if (!awsSettings?.aws_region) {
          throw new Error("AWS settings require a region");
        }
        if (!awsSettings?.aws_assume_role_arn) {
          throw new Error("AWS settings require an assume role ARN");
        }
        if (!awsSettings?.models || awsSettings.models.length === 0) {
          throw new Error("AWS settings require at least one model");
        }
      } else if (selectedProvider === "openai") {
        if (!openaiSettings?.openai_api_key) {
          throw new Error("OpenAI settings require an API key");
        }
        if (!openaiSettings?.openai_project) {
          throw new Error("OpenAI settings require a project ID");
        }
        if (!openaiSettings?.models || openaiSettings.models.length === 0) {
          throw new Error("OpenAI settings require at least one model");
        }
      } else if (selectedProvider === "deepseek") {
        if (!deepseekSettings?.deepseek_api_key) {
          throw new Error("DeepSeek settings require an API key");
        }
        if (!deepseekSettings?.models || deepseekSettings.models.length === 0) {
          throw new Error("DeepSeek settings require at least one model");
        }
      } else if (selectedProvider === "genbi") {
        if (!genbiSettings?.openai_api_key) {
          throw new Error("GenBI settings require an API key");
        }
        if (!genbiSettings?.openai_project) {
          throw new Error("GenBI settings require a project ID");
        }
        if (!genbiSettings?.models || genbiSettings.models.length === 0) {
          throw new Error("GenBI settings require at least one model");
        }
      }

      return {
        awsSettings:
          selectedProvider === "aws" ? { ...awsSettings } : undefined,
        openaiSettings:
          selectedProvider === "openai" ? { ...openaiSettings } : undefined,
        genbiSettings:
          selectedProvider === "genbi" ? { ...genbiSettings } : undefined,
        deepseekSettings:
          selectedProvider === "deepseek" ? { ...deepseekSettings } : undefined,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  const handleSubmit = async (values: EditTenantFormValues) => {
    if (!onSubmit) return;

    try {
      const { awsSettings, openaiSettings, genbiSettings, deepseekSettings } =
        await validateAISettings();

      const formattedValues: UpdateTenantDTO = {
        tenant_name: values.tenant_name,
        tenant_code: values.tenant_code,
        max_users: values.max_users,
        valid_until: values.valid_until?.toDate(),
        model_provider: values.model_provider,
        status: values.status,
        aws_settings: awsSettings,
        openai_settings: openaiSettings,
        genbi_settings: genbiSettings,
        deepseek_settings: deepseekSettings,
      };

      await onSubmit(formattedValues);
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === "Invalid JSON format") {
          message.error("Please check the JSON format of your settings");
        } else {
          message.error(error.message);
        }
      }
    }
  };

  const handleProviderChange = (value: ModelProvider) => {
    setSelectedProvider(value);
    form.setFieldValue("model_provider", value);
    // Clear errors for other providers
    setAwsSettingsError(undefined);
    setOpenaiSettingsError(undefined);
    setGenbiSettingsError(undefined);
    setDeepseekSettingsError(undefined);
  };

  const handleAwsSettingsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    try {
      const value = e.target.value;
      if (value.trim()) {
        JSON.parse(value);
        setAwsSettingsError(undefined);
      }
    } catch (error) {
      setAwsSettingsError("Invalid JSON format");
    }
  };

  const handleOpenaiSettingsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    try {
      const value = e.target.value;
      if (value.trim()) {
        JSON.parse(value);
        setOpenaiSettingsError(undefined);
      }
    } catch (error) {
      setOpenaiSettingsError("Invalid JSON format");
    }
  };

  const handleGenbiSettingsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    try {
      const value = e.target.value;
      if (value.trim()) {
        JSON.parse(value);
        setGenbiSettingsError(undefined);
      }
    } catch (error) {
      setGenbiSettingsError("Invalid JSON format");
    }
  };

  const handleDeepseekSettingsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    try {
      const value = e.target.value;
      if (value.trim()) {
        JSON.parse(value);
        setDeepseekSettingsError(undefined);
      }
    } catch (error) {
      setDeepseekSettingsError("Invalid JSON format");
    }
  };

  return (
    <>
      <StyledForm
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={readonly}
      >
        <Form.Item
          label="Tenant Name"
          name="tenant_name"
          rules={[{ required: true, message: "Please input tenant name!" }]}
        >
          <Input placeholder="Enter tenant name" />
        </Form.Item>

        <Form.Item
          label="Tenant Code"
          name="tenant_code"
          rules={[{ required: true, message: "Please input tenant code!" }]}
        >
          <Input placeholder="Enter tenant code" />
        </Form.Item>

        <Form.Item
          label="Max Users"
          name="max_users"
          rules={[{ required: true, message: "Please input max users!" }]}
        >
          <InputNumber
            min={1}
            style={{ width: "100%" }}
            placeholder="Enter max users"
          />
        </Form.Item>

        <Form.Item
          label="Valid Until"
          name="valid_until"
          rules={[
            { required: true, message: "Please select valid until date!" },
          ]}
        >
          <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
        </Form.Item>

        <Form.Item
          label="Model Provider"
          name="model_provider"
          rules={[
            { required: true, message: "Please select a model provider!" },
          ]}
          tooltip="Choose the AI model provider for this tenant."
        >
          <Select
            placeholder="Select model provider"
            disabled={readonly}
            onChange={handleProviderChange}
            options={[
              { value: "genbi", label: "GenBI" },
              { value: "openai", label: "OpenAI" },
              { value: "aws", label: "AWS" },
              { value: "deepseek", label: "DeepSeek" },
            ]}
          />
        </Form.Item>

        {!readonly && (
          <>
            <StyledDivider orientation="left">AI Settings</StyledDivider>

            {selectedProvider === "genbi" && (
              <Form.Item
                label="GenBI AI Settings"
                name="genbi_settings"
                validateStatus={genbiSettingsError ? "error" : undefined}
                help={genbiSettingsError}
                tooltip="Enter GenBI AI settings in JSON format. Required field: genbi_api_key"
              >
                <JsonEditor
                  value={form.getFieldValue("genbi_settings")}
                  onChange={(value) => {
                    form.setFieldValue("genbi_settings", value || "");
                    handleGenbiSettingsChange({
                      target: { value: value || "" },
                    } as React.ChangeEvent<HTMLTextAreaElement>);
                  }}
                  height={200}
                  showError={true}
                  errorMessage={genbiSettingsError}
                />
              </Form.Item>
            )}

            {selectedProvider === "aws" && (
              <Form.Item
                label="AWS Settings"
                name="aws_settings"
                validateStatus={awsSettingsError ? "error" : undefined}
                help={awsSettingsError}
                tooltip="Enter AWS settings in JSON format. Required fields: aws_access_key, aws_secret_key"
              >
                <JsonEditor
                  value={form.getFieldValue("aws_settings")}
                  onChange={(value) => {
                    form.setFieldValue("aws_settings", value || "");
                    handleAwsSettingsChange({
                      target: { value: value || "" },
                    } as React.ChangeEvent<HTMLTextAreaElement>);
                  }}
                  height={200}
                  showError={true}
                  errorMessage={awsSettingsError}
                />
              </Form.Item>
            )}

            {selectedProvider === "openai" && (
              <Form.Item
                label="OpenAI Settings"
                name="openai_settings"
                validateStatus={openaiSettingsError ? "error" : undefined}
                help={openaiSettingsError}
                tooltip="Enter OpenAI settings in JSON format. Required field: openai_api_key"
              >
                <JsonEditor
                  value={form.getFieldValue("openai_settings")}
                  onChange={(value) => {
                    form.setFieldValue("openai_settings", value || "");
                    handleOpenaiSettingsChange({
                      target: { value: value || "" },
                    } as React.ChangeEvent<HTMLTextAreaElement>);
                  }}
                  height={200}
                  showError={true}
                  errorMessage={openaiSettingsError}
                />
              </Form.Item>
            )}

            {selectedProvider === "deepseek" && (
              <Form.Item
                label="DeepSeek Settings"
                name="deepseek_settings"
                validateStatus={deepseekSettingsError ? "error" : undefined}
                help={deepseekSettingsError}
                tooltip="Enter DeepSeek settings in JSON format. Required field: deepseek_api_key"
              >
                <JsonEditor
                  value={form.getFieldValue("deepseek_settings")}
                  onChange={(value) => {
                    form.setFieldValue("deepseek_settings", value || "");
                    handleDeepseekSettingsChange({
                      target: { value: value || "" },
                    } as React.ChangeEvent<HTMLTextAreaElement>);
                  }}
                  height={200}
                  showError={true}
                  errorMessage={deepseekSettingsError}
                />
              </Form.Item>
            )}
          </>
        )}

        {readonly && (
          <>
            <StyledDivider orientation="left">AI Settings</StyledDivider>

            {selectedProvider === "genbi" && tenant.genbi_settings && (
              <Form.Item label="GenBI AI Settings">
                <JsonEditor
                  value={JSON.stringify(tenant.genbi_settings, null, 2)}
                  height={200}
                  readOnly={true}
                  showFormatButton={false}
                />
              </Form.Item>
            )}

            {selectedProvider === "aws" && tenant.aws_settings && (
              <Form.Item label="AWS Settings">
                <JsonEditor
                  value={JSON.stringify(tenant.aws_settings, null, 2)}
                  height={200}
                  readOnly={true}
                  showFormatButton={false}
                />
              </Form.Item>
            )}

            {selectedProvider === "openai" && tenant.openai_settings && (
              <Form.Item label="OpenAI Settings">
                <JsonEditor
                  value={JSON.stringify(tenant.openai_settings, null, 2)}
                  height={200}
                  readOnly={true}
                  showFormatButton={false}
                />
              </Form.Item>
            )}

            {selectedProvider === "deepseek" && tenant.deepseek_settings && (
              <Form.Item label="DeepSeek Settings">
                <JsonEditor
                  value={JSON.stringify(tenant.deepseek_settings, null, 2)}
                  height={200}
                  readOnly={true}
                  showFormatButton={false}
                />
              </Form.Item>
            )}
          </>
        )}

        <Form.Item
          label="Status"
          name="status"
          rules={[{ required: true, message: "Please select status!" }]}
        >
          <Radio.Group disabled={readonly}>
            <Radio value="active">Active</Radio>
            <Radio value="inactive">Inactive</Radio>
          </Radio.Group>
        </Form.Item>

        {!readonly && (
          <Form.Item
            style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}
          >
            <Space>
              <Button onClick={onCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Update
              </Button>
            </Space>
          </Form.Item>
        )}
      </StyledForm>
      {readonly && (
        <div style={{ marginTop: 24, textAlign: "right" }}>
          <Button onClick={onCancel}>Close</Button>
        </div>
      )}
    </>
  );
};

export default EditTenantForm;
